import AddCircleIcon from "@mui/icons-material/AddCircle";
import CloseIcon from "@mui/icons-material/Close";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import HomeIcon from "@mui/icons-material/Home";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import MenuIcon from "@mui/icons-material/Menu";
import AppBar from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import Drawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import { useTheme } from "@mui/material/styles";
import Toolbar from "@mui/material/Toolbar";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import { ReactNode, useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import { GroupSelect } from "../filter/GroupSelect";
import { useGeotabClient } from "../GeotabClientContext";
import LanguageToggle from "../LanguageToggle";
import { LoginPage } from "../login/LoginPage";
import { AddInNameContext } from "./AddInNameContext";
import { DocsDrawer } from "./DocsDrawer";
import { InfoDialog } from "./InfoDialog";
import { PageHelp } from "./PageHelp";
import SessionSwitcher from "./SessionSwitcher";
import { useGroupFilterMemory } from "@/hooks/groups/useGroupFilterMemory";

interface Props {
  title: string;
  content: ReactNode | ReactNode[];
  handleHomeClick?: () => void;
  docs?: ReactNode;
  /**
   * The button that opens the list of all pages, top left.
   *
   * Passed in rather than built here: only the home page knows what pages there are and how this
   * reader has arranged them, and only it can switch between them.
   */
  appMenuButton?: ReactNode;
  /** Add-in name whose help the help button shows; defaults to the page registered in `renderAddIn`. */
  helpId?: string;
}

export default function PageHeader({
  title,
  content,
  handleHomeClick,
  docs,
  appMenuButton,
  helpId,
}: Props) {
  const { t } = useTranslation();
  const { geotabView } = useGeotabClient();
  const addInName = useContext(AddInNameContext);
  const [loginPage, setLoginPage] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [docsOpen, setDocsOpen] = useState(false);
  const [infoOpen, setInfoOpen] = useState(false);

  const groupFilterMemory = useGroupFilterMemory();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down(1400));

  const handleDrawerToggle = () => {
    setDrawerOpen(prev => !prev);
  };

  const drawerContent = (
    <Box
      sx={{
        height: "100%",
        minWidth: 250,
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.primary.contrastText,
        p: 2,
        display: "flex",
        flexDirection: "column",
        gap: 2,
      }}
    >
      <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
        <IconButton onClick={handleDrawerToggle}>
          <CloseIcon color="secondary" />
        </IconButton>
      </Box>

      {!geotabView && (
        <>
          <SessionSwitcher isMobile={isMobile} />
          <Button
            fullWidth
            startIcon={loginPage ? <HomeIcon /> : <AddCircleIcon />}
            onClick={() => setLoginPage(!loginPage)}
            variant="contained"
          >
            {loginPage ? "Back to App" : "Login"}
          </Button>

          {!loginPage && handleHomeClick && (
            <Button
              fullWidth
              variant="contained"
              startIcon={<HomeIcon />}
              onClick={handleHomeClick}
            >
              {"Go Home"}
            </Button>
          )}
        </>
      )}

      <GroupSelect memory={groupFilterMemory} />
      <LanguageToggle />
    </Box>
  );
  return (
    <Stack sx={{ height: "100%", overflow: "auto" }}>
      <AppBar position="sticky" sx={{ height: "50px", zIndex: theme.zIndex.appBar }}>
        <Toolbar variant="dense">
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              width: "100%",
            }}
          >
            {/* Title and menu toggle */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {appMenuButton}
              {isMobile && (
                <IconButton color="inherit" onClick={handleDrawerToggle}>
                  <MenuIcon />
                </IconButton>
              )}
              <Typography variant="h6" noWrap>
                {title}
              </Typography>
              <Tooltip title={t("pageHelp.button")}>
                <IconButton color="inherit" size="small" onClick={() => setDocsOpen(true)} aria-label={t("pageHelp.button")}>
                  <HelpOutlineIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title={t("appInfo.button")}>
                <IconButton color="inherit" size="small" onClick={() => setInfoOpen(true)} aria-label={t("appInfo.button")}>
                  <InfoOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <LanguageToggle />
            </Box>

            {/* Desktop Center + Right Elements */}
            {!isMobile && !geotabView && (
              <Box
                sx={{
                  position: "absolute",
                  left: "50%",
                  transform: "translateX(-50%)",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <SessionSwitcher />
                <Button variant="contained" onClick={() => setLoginPage(!loginPage)}>
                  {loginPage ? <HomeIcon /> : <AddCircleIcon />}
                </Button>
                {!loginPage && handleHomeClick && (
                  <Button sx={{ ml: 2 }} variant="contained" onClick={handleHomeClick}>
                    <HomeIcon />
                  </Button>
                )}
              </Box>
            )}

            {!isMobile && <GroupSelect memory={groupFilterMemory} />}
          </Box>
        </Toolbar>
      </AppBar>

      <Drawer anchor="left" open={drawerOpen} onClose={handleDrawerToggle}>
        {drawerContent}
      </Drawer>

      <DocsDrawer title={title} open={docsOpen} onClose={() => setDocsOpen(false)}>
        <PageHelp pageId={helpId ?? addInName} />
        {docs && (
          <>
            <Divider sx={{ my: 3 }} />
            {docs}
          </>
        )}
      </DocsDrawer>
      <InfoDialog open={infoOpen} onClose={() => setInfoOpen(false)} />

      <Box sx={{ flex: 1, minHeight: 0, width: "100%" }}>
        {loginPage ? <LoginPage onLoggedIn={() => setLoginPage(false)} /> : content}
      </Box>
    </Stack>
  );
}
